import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://flowforgemechanical.ca';
const DEFAULT_IMAGE = `${SITE}/flow-forge-logo.png`;
const SITE_NAME = 'Flow Forge Mechanical';

// One source of truth for per-page SEO: exactly one title + one description +
// a self-referencing canonical + Open Graph + Twitter tags. Uses react-helmet-async.
// Resolve any image path to an absolute https URL on the canonical domain.
const absUrl = (img) => {
  if (!img) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(img)) return img;
  return `${SITE}${img.startsWith('/') ? '' : '/'}${img}`;
};

export const Seo = ({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  type = 'website',
  jsonLd = null,
  preloadImage = null,
}) => {
  const url = `${SITE}${path === '/' ? '/' : path}`;
  const imageUrl = absUrl(image);
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const preloadBase = preloadImage ? preloadImage.replace(/\.jpe?g$/i, '') : null;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {preloadBase && (
        <link
          rel="preload"
          as="image"
          href={`${preloadBase}-1280.webp`}
          imageSrcSet={`${preloadBase}-768.webp 768w, ${preloadBase}-1280.webp 1280w`}
          imageSizes="100vw"
          type="image/webp"
          fetchPriority="high"
        />
      )}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {blocks.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
